/**
 * صفحه‌ی پرداختِ عمومیِ فاکتور با رمزارز
 * =======================================
 * این صفحه برای **مشتریِ پاسارگاد میزبان** است، نه کاربرِ ارزینو — پس
 * عمومی است و ورود نمی‌خواهد. شناسه‌ی فاکتور (token) خودش رمزِ دسترسی است.
 *
 * جریان: مبلغ تومانی را نشان می‌دهد ← مشتری ارز را انتخاب می‌کند ← آدرس،
 * مبلغِ دقیق و QR + شمارشِ معکوس ← هر چند ثانیه وضعیت را می‌پرسد ← با
 * «پرداخت‌شده» پیام موفقیت و بازگشت به سایتِ فاکتور.
 */
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import QRCode from 'qrcode'
import { get, post } from '../lib/api'
import { clsx, num } from '../lib/format'

type Charge = {
  token: string; orderId: string; amountIrt: number; status: string
  coins: string[]; networks: Record<string, string[]>
  symbol: string; network: string; address: string
  expectedAmount: number; rateIrt: number; receivedAmount: number
  expiresAt: string | null; returnUrl: string; payUrl: string
}

const COIN_FA: Record<string, string> = {
  USDT: 'تتر', BTC: 'بیت‌کوین', ETH: 'اتریوم', TRX: 'ترون', TON: 'تون‌کوین',
  BNB: 'بی‌ان‌بی', SOL: 'سولانا', DOGE: 'دوج‌کوین', LTC: 'لایت‌کوین',
}

function Qr({ text }: { text: string }) {
  const [url, setUrl] = useState('')
  useEffect(() => {
    if (!text) return setUrl('')
    QRCode.toDataURL(text, { width: 320, margin: 1,
      color: { dark: '#05070F', light: '#FFFFFF' }, errorCorrectionLevel: 'M' })
      .then(setUrl).catch(() => setUrl(''))
  }, [text])
  if (!url) return <div className="skeleton h-40 w-40 rounded-xl" />
  return <img src={url} width={160} height={160} alt="QR"
    className="rounded-xl border-4 border-white bg-white" />
}

function Copy({ text }: { text: string }) {
  const [done, setDone] = useState(false)
  return (
    <button onClick={() => { navigator.clipboard?.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500) }}
      className="rounded-lg border border-white/15 px-2 py-1 text-[11px] text-slate-300 transition hover:bg-white/5">
      {done ? 'کپی شد ✓' : 'کپی'}
    </button>
  )
}

function Countdown({ iso, onExpire }: { iso: string | null; onExpire: () => void }) {
  const [left, setLeft] = useState(0)
  useEffect(() => {
    if (!iso) return
    const end = new Date(iso).getTime()
    const tick = () => {
      const s = Math.max(0, Math.floor((end - Date.now()) / 1000))
      setLeft(s)
      if (s <= 0) onExpire()
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [iso])
  const m = String(Math.floor(left / 60)).padStart(2, '0')
  const s = String(left % 60).padStart(2, '0')
  return <span className="num" dir="ltr">{m}:{s}</span>
}

export default function Pay() {
  const { token = '' } = useParams()
  const [ch, setCh] = useState<Charge | null>(null)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [pickCoin, setPickCoin] = useState('')
  const [pickNet, setPickNet] = useState('')
  const timer = useRef<number | null>(null)

  const load = useCallback(() => {
    get<Charge>(`/commerce/charge/${token}`)
      .then((r) => { setCh(r); setErr('') })
      .catch((e: any) => setErr(e?.message || 'فاکتور یافت نشد'))
  }, [token])

  useEffect(() => { load() }, [load])

  // وقتی منتظر پرداختیم، وضعیت را دوره‌ای می‌پرسیم تا لحظه‌ی نشستنِ پول را ببینیم
  useEffect(() => {
    if (ch?.status === 'pending') {
      timer.current = window.setInterval(load, 6000)
      return () => { if (timer.current) window.clearInterval(timer.current) }
    }
  }, [ch?.status, load])

  // با «پرداخت‌شده»، پس از مکث کوتاه به سایتِ فاکتور برمی‌گردیم
  useEffect(() => {
    if (ch?.status === 'paid' && ch.returnUrl) {
      const id = setTimeout(() => { window.location.href = ch.returnUrl }, 4000)
      return () => clearTimeout(id)
    }
  }, [ch?.status])

  const submitCoin = async () => {
    if (!pickCoin) return
    setBusy(true)
    try {
      const r = await post<Charge>(`/commerce/charge/${token}/select`,
        { symbol: pickCoin, network: pickNet })
      setCh(r)
    } catch (e: any) { setErr(e.message) } finally { setBusy(false) }
  }

  const shell = (body: ReactNode) => (
    <div dir="rtl" className="grid min-h-screen place-items-center bg-ink-950 p-4">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-ink-900/70 p-6 shadow-xl">
        <div className="mb-4 text-center">
          <div className="text-lg font-extrabold text-white">پرداخت با رمزارز</div>
          <div className="text-[11px] text-slate-500">پردازش امن توسط ارزینو</div>
        </div>
        {body}
      </div>
    </div>
  )

  if (err && !ch) return shell(<p className="text-center text-sm text-down">{err}</p>)
  if (!ch) return shell(<div className="skeleton h-40 w-full rounded-xl" />)

  const irt = <span className="num" dir="ltr">{num(ch.amountIrt)}</span>

  if (ch.status === 'paid') {
    return shell(
      <div className="text-center">
        <div className="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-full bg-up/15 text-2xl">✓</div>
        <div className="font-bold text-up">پرداخت با موفقیت انجام شد</div>
        <p className="mt-2 text-xs leading-6 text-slate-400">
          فاکتور شما ({ch.orderId}) پرداخت شد. در حال بازگشت به سایت…
        </p>
      </div>)
  }
  if (ch.status === 'expired' || ch.status === 'cancelled') {
    return shell(<p className="text-center text-sm text-slate-300">
      مهلت این فاکتور به پایان رسید. لطفاً از سایتِ فروشنده دوباره اقدام کنید.</p>)
  }
  if (ch.status === 'underpaid') {
    return shell(<div className="text-center text-sm leading-7 text-gold">
      مبلغِ رسیده کمتر از مبلغِ فاکتور بود. برای پیگیری با پشتیبانی تماس بگیرید.
      <div className="num mt-2 text-xs text-slate-400" dir="ltr">
        رسیده: {ch.receivedAmount} / لازم: {ch.expectedAmount} {ch.symbol}</div>
    </div>)
  }

  // ── انتخاب ارز
  if (ch.status === 'new') {
    return shell(
      <div>
        <div className="mb-4 rounded-xl border border-white/[.06] bg-white/[.02] p-3 text-center">
          <div className="text-[11px] text-slate-500">مبلغ فاکتور</div>
          <div className="mt-1 text-xl font-extrabold text-white">{irt} <span className="text-sm text-slate-500">تومان</span></div>
        </div>
        <div className="mb-2 text-xs text-slate-400">با کدام ارز پرداخت می‌کنید؟</div>
        <div className="mb-3 grid grid-cols-3 gap-2">
          {ch.coins.map((c) => (
            <button key={c} onClick={() => { setPickCoin(c); setPickNet((ch.networks[c] || [])[0] || '') }}
              className={clsx('rounded-xl border py-2.5 text-sm font-bold transition',
                pickCoin === c ? 'border-brand-500 bg-brand-500/15 text-brand-200'
                  : 'border-white/10 bg-white/[.03] text-slate-300 hover:border-white/20')}>
              <div>{COIN_FA[c] || c}</div>
              <div className="num text-[10px] text-slate-500" dir="ltr">{c}</div>
            </button>
          ))}
        </div>
        {pickCoin && (ch.networks[pickCoin] || []).length > 1 && (
          <div className="mb-3">
            <div className="mb-1 text-[11px] text-slate-500">شبکه</div>
            <div className="flex flex-wrap gap-2">
              {(ch.networks[pickCoin] || []).map((n) => (
                <button key={n} onClick={() => setPickNet(n)}
                  className={clsx('num rounded-lg px-2.5 py-1 text-[11px]', pickNet === n
                    ? 'bg-brand-500/20 text-brand-200 ring-1 ring-brand-500/40'
                    : 'border border-white/10 text-slate-400')} dir="ltr">{n}</button>
              ))}
            </div>
          </div>
        )}
        {err && <p className="mb-2 text-[11px] text-down">{err}</p>}
        <button onClick={submitCoin} disabled={!pickCoin || busy}
          className="btn-primary w-full !py-3 disabled:opacity-50">
          {busy ? 'در حال آماده‌سازی…' : 'ادامه'}
        </button>
      </div>)
  }

  // ── منتظر پرداخت (pending)
  return shell(
    <div>
      <div className="mb-3 flex items-center justify-between text-[11px] text-slate-400">
        <span>مبلغ فاکتور: {irt} تومان</span>
        <span>مهلت: <Countdown iso={ch.expiresAt} onExpire={load} /></span>
      </div>
      <div className="mb-3 flex justify-center"><Qr text={ch.address} /></div>
      <div className="space-y-2 rounded-xl border border-white/[.06] bg-white/[.02] p-3 text-[11px]">
        <div className="flex items-center justify-between gap-2">
          <span className="text-slate-500">دقیقاً این مبلغ را بفرستید</span>
          <span className="flex items-center gap-2">
            <span className="num font-extrabold text-brand-200" dir="ltr">{ch.expectedAmount} {ch.symbol}</span>
            <Copy text={String(ch.expectedAmount)} />
          </span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-slate-500">شبکه</span>
          <span className="num text-slate-200" dir="ltr">{ch.network}</span>
        </div>
        <div className="border-t border-white/[.06] pt-2">
          <div className="mb-1 text-slate-500">آدرس واریز</div>
          <div className="flex items-center gap-2">
            <span className="num break-all text-[10px] text-slate-200" dir="ltr">{ch.address}</span>
            <Copy text={ch.address} />
          </div>
        </div>
      </div>
      <div className="mt-3 rounded-xl border border-gold/25 bg-gold/[.06] p-3 text-[11px] leading-6 text-gold">
        <b>حتماً همین مبلغِ دقیق را بفرستید</b> — تا واریز شما خودکار به این
        فاکتور وصل شود. ارسال روی شبکه‌ی <span className="num" dir="ltr">{ch.network}</span> باشد.
        این صفحه پس از تأیید شبکه، خودکار به‌روز می‌شود.
      </div>
      <div className="mt-3 flex items-center justify-center gap-2 text-[11px] text-slate-500">
        <span className="h-2 w-2 animate-pulse rounded-full bg-up" /> در انتظار دریافت پرداخت…
      </div>
    </div>)
}
